import React, { useEffect, useMemo, useState } from 'react';
import VacationRequestForm from '../components/VacationRequestForm';
import VacationRequestList from '../components/VacationRequestList';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store/store';
import { fetchAllUsers } from '../slices/authSlice';
import { fetchWorkSchedules } from '../slices/workScheduleSlice';
import { getVacationRequests } from '../services/vacationRequestService';
import {
  getVacationAccountSummary,
} from '../utils/vacationUtils';

interface VacationRequest {
  _id: string;
  userId?: string | { _id: string };
  startDate: string;
  endDate: string;
  dayFraction?: number;
  vacationDays?: number;
  vacationDaysByYear?: Record<string, number>;
  status: 'pending' | 'approved' | 'declined';
}

const VacationPage = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [refreshFlag, setRefreshFlag] = useState(false);
  const user = useSelector((state: RootState) => state.auth.user);
  const token = useSelector((state: RootState) => state.auth.token);
  const allUsers = useSelector((state: RootState) => state.auth.allUsers || []);
  const schedules = useSelector((state: RootState) => state.work.schedules);
  const isAdmin = user?.role === 'admin';
  const userId = user?._id || '';
  const [selectedVacationUserId, setSelectedVacationUserId] = useState(userId);
  const [requests, setRequests] = useState<VacationRequest[]>([]);
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedRequestYear, setSelectedRequestYear] = useState<number | 'all'>('all');
  const [showReviewedRequests, setShowReviewedRequests] = useState(true);

  useEffect(() => {
    dispatch(fetchWorkSchedules());
  }, [dispatch]);

  useEffect(() => {
    if (isAdmin) {
      dispatch(fetchAllUsers());
    }
  }, [dispatch, isAdmin]);

  useEffect(() => {
    if (!selectedVacationUserId && userId) {
      setSelectedVacationUserId(userId);
    }
  }, [selectedVacationUserId, userId]);

  useEffect(() => {
    const loadRequests = async () => {
      if (!token) return;

      try {
        const data = await getVacationRequests(token, true);
        setRequests(data);
      } catch (error) {
        console.error('Fehler beim Laden des Urlaubskontos:', error);
      }
    };

    loadRequests();
  }, [refreshFlag, token]);

  const triggerRefresh = () => setRefreshFlag((prev) => !prev);

  const availableYears = useMemo(() => {
    const years = new Set<number>([currentYear, currentYear + 1]);

    for (let year = 2023; year <= currentYear; year += 1) {
      years.add(year);
    }

    requests.forEach((request) => {
      const startYear = new Date(request.startDate).getFullYear();
      const endYear = new Date(request.endDate).getFullYear();

      if (!Number.isNaN(startYear)) years.add(startYear);
      if (!Number.isNaN(endYear)) years.add(endYear);
    });

    return Array.from(years).sort((a, b) => b - a);
  }, [currentYear, requests]);

  const filteredRequests = useMemo(() => {
    const selectedUserForList = isAdmin ? selectedVacationUserId : userId;

    return requests
      .filter((request) => {
        const requestUserId =
          typeof request.userId === 'string' ? request.userId : request.userId?._id;
        const startYear = new Date(request.startDate).getFullYear();
        const endYear = new Date(request.endDate).getFullYear();
        const matchesUser = !selectedUserForList || requestUserId === selectedUserForList;
        const matchesYear =
          selectedRequestYear === 'all' ||
          startYear === selectedRequestYear ||
          endYear === selectedRequestYear;
        const matchesReviewStatus = showReviewedRequests || request.status === 'pending';

        return matchesUser && matchesYear && matchesReviewStatus;
      })
      .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [isAdmin, requests, selectedRequestYear, selectedVacationUserId, showReviewedRequests, userId]);

  const vacationSummary = useMemo(() => {
    const accountUserId = isAdmin ? selectedVacationUserId : userId;

    if (!accountUserId) {
      return {
        entitlement: 0,
        annualEntitlement: 0,
        previousYearRemaining: 0,
        carried: 0,
        approved: 0,
        pending: 0,
        remaining: 0,
      };
    }

    const ownRequests = requests.filter((request) => {
      if (!request.userId) return true;
      if (typeof request.userId === 'string') return request.userId === accountUserId;
      return request.userId._id === accountUserId;
    });

    const accountUser = allUsers.find((currentUser) => currentUser._id === accountUserId) || user;
    return getVacationAccountSummary(
      schedules,
      accountUserId,
      ownRequests,
      selectedYear,
      accountUser || undefined
    );
  }, [allUsers, isAdmin, requests, schedules, selectedVacationUserId, selectedYear, user, userId]);

  return (
    <div className="container mt-4">
      <h2>Urlaubsantrag stellen</h2>
      {isAdmin && (
        <div className="mb-3" style={{ maxWidth: 320 }}>
          <label className="form-label">Nutzer</label>
          <select
            className="form-select"
            value={selectedVacationUserId}
            onChange={(event) => setSelectedVacationUserId(event.target.value)}
          >
            <option value="">Bitte auswählen</option>
            {allUsers.map((currentUser) => (
              <option key={currentUser._id} value={currentUser._id}>
                {currentUser.username}
              </option>
            ))}
          </select>
        </div>
      )}
      <div className="card p-3 mb-4 shadow-sm">
        <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-3">
          <h5 className="mb-0">Urlaubskonto {selectedYear}</h5>
          <select
            className="form-select"
            style={{ width: 140 }}
            value={selectedYear}
            onChange={(event) => setSelectedYear(Number(event.target.value))}
          >
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
        <div className="d-flex gap-4 flex-wrap">
          <div>
            <strong>Anspruch:</strong> {vacationSummary.entitlement} Tage
          </div>
          <div>
            <strong>Urlaubstage aus letztem Jahr:</strong> {vacationSummary.previousYearRemaining} Tage
          </div>
          <div>
            <strong>Genehmigt:</strong> {vacationSummary.approved} Tage
          </div>
          <div>
            <strong>Offen beantragt:</strong> {vacationSummary.pending} Tage
          </div>
          <div>
            <strong>Verbleibend:</strong> {vacationSummary.remaining} Tage
          </div>
        </div>
      </div>
      <VacationRequestForm
        targetUserId={isAdmin ? selectedVacationUserId : undefined}
        onSuccess={triggerRefresh}
      />
      <hr />
      <div className="d-flex align-items-center justify-content-between gap-3 flex-wrap mb-2">
        <h4 className="mb-0">Urlaubsanträge</h4>
        <div className="d-flex align-items-center gap-3 flex-wrap">
          <label className="form-check report-export-option mb-0" htmlFor="vacation-show-approved">
            <input
              className="form-check-input report-export-checkbox"
              type="checkbox"
              id="vacation-show-approved"
              checked={showReviewedRequests}
              onChange={(event) => setShowReviewedRequests(event.target.checked)}
            />
            <span className="form-check-label">
              Genehmigte und abgelehnte Anträge anzeigen
            </span>
          </label>
          <select
            className="form-select"
            style={{ width: 140 }}
            value={selectedRequestYear}
            onChange={(event) =>
              setSelectedRequestYear(
                event.target.value === 'all' ? 'all' : Number(event.target.value)
              )
            }
          >
            <option value="all">Gesamt</option>
            {availableYears.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>
      </div>
      <VacationRequestList
        requests={filteredRequests}
        refreshFlag={refreshFlag}
        onEdit={triggerRefresh}
        onDelete={triggerRefresh}
        currentUserId={userId}
        isAdmin={!!isAdmin}
      />
    </div>
  );
};

export default VacationPage;
